import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { defer, delay, map, startWith, tap } from 'rxjs';

import { InputMaskDirective, timeMask } from '../../../sdk/forms/mask';
import { DEFAULT_TIME_INTERVAL, generateIntervalTimeList } from '../utils';

@Component({
  selector: 'rms-time-picker',
  imports: [
    MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatIconModule,
    InputMaskDirective,
    AsyncPipe,
    NgClass,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CdkVirtualScrollViewport,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TimePickerComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './time-picker.component.html',
})
export class TimePickerComponent implements ControlValueAccessor, OnInit {
  @Input() public placeholder = 'HH:mm';

  @Input() public interval = DEFAULT_TIME_INTERVAL;

  @ViewChild('timeInput') private timeInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) private autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild(CdkVirtualScrollViewport) private viewport?: CdkVirtualScrollViewport;

  public readonly ITEM_SIZE = 48;
  public readonly MAX_VIEWPORT_HEIGHT = this.ITEM_SIZE * 5;

  private readonly destroy$ = inject(DestroyRef);

  public timeControl = new FormControl<string>('');
  public filteredOptions = defer(() =>
    this.timeControl.valueChanges.pipe(
      startWith(''),
      map((value) => (value ? this.filter(value) : this.timeList.slice()))
    )
  );

  public viewportHeight$ = defer(() =>
    this.filteredOptions.pipe(
      map((options) => Math.min(options.length * this.ITEM_SIZE, this.MAX_VIEWPORT_HEIGHT))
    )
  );

  public selectedOptions$ = defer(() =>
    this.timeControl.valueChanges.pipe(
      startWith(this.timeControl.value),
      tap((value) => {
        if (!value) {
          return null;
        }
        const filterValue = value.toLowerCase();
        const [inputHour, inputMinute] = filterValue.split(':');

        if (inputHour?.length === 2 && inputMinute?.length === 2) {
          return filterValue;
        }

        return null;
      })
    )
  );

  private timeList: string[] = [];
  private onChange?: (value: string) => void;
  private onTouched?: () => void;

  public timeMask = (value: string): string => timeMask(value, this.timeList);

  ngOnInit(): void {
    this.timeList = generateIntervalTimeList(this.interval);
    this.timeControl.valueChanges
      .pipe(
        /*
         * writeValue value may be invalid for our time interval.
         * Therefore, we need to wait 100 ms for onChange to be set
         * then we emit masked initial value to parent control
         * */
        delay(100),
        tap((value) => {
          this.onTouched?.();
          this.onChange?.(value ?? '');
        }),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe();
  }

  writeValue(value: string): void {
    this.timeControl.setValue(this.timeMask(value) ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.timeControl.disable() : this.timeControl.enable();
  }

  public onPanelOpened(): void {
    this.viewport?.checkViewportSize();
  }

  public onEnterKeyPress(): void {
    const currentValue = this.timeControl.value ?? '';
    const digits = currentValue.replace(/\D/g, '');

    const paddedDigits = digits.padEnd(4, '0');
    const paddedValue = `${paddedDigits.substring(0, 2)}:${paddedDigits.substring(2, 4)}`;

    const maskedValue = this.timeMask(paddedValue);
    if (maskedValue !== currentValue) {
      this.timeControl.setValue(maskedValue);
    }
    this.autocompleteTrigger.closePanel();
    this.timeInput.nativeElement.blur();
  }

  private filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    const [inputHour, inputMinute] = filterValue.split(':');

    if (inputHour?.length === 2 && inputMinute?.length === 2) {
      return this.timeList.slice();
    }

    return this.timeList.filter((option) => {
      const optionHour = option.split(':')[0];

      if (!inputHour) {
        return true;
      }

      if (!inputMinute) {
        return optionHour.startsWith(inputHour);
      }

      return option.startsWith(filterValue);
    });
  }
}
